<template>
  <!--
    Painting-in-progress status card. Replaces a "Painting…" button
    label with a richer affordance: pulsing portrait frame + spinner +
    elapsed seconds + ~typical hint. Mirrors CardLightbox's status
    indicator so the operator has a sense of progress on a 30-90s
    LLM call instead of staring at a flat label.
  -->
  <div class="painting-status">
    <div class="painting-frame">
      <div class="frame-glow"></div>
      <div class="spinner"></div>
    </div>
    <div class="painting-text">
      <div class="painting-title">Painting your portrait…</div>
      <div class="painting-meta">
        <span>{{ elapsed }}s</span>
        <span class="dot">·</span>
        <span>~{{ typical }}s typical</span>
      </div>
      <div v-if="elapsed > typical + 15" class="painting-hint">
        Sometimes the model takes a few extra seconds - hang tight.
      </div>
    </div>
  </div>
</template>

<script setup>
defineProps({
  elapsed: { type: Number, required: true },
  typical: { type: Number, default: 45 },
})
</script>

<style scoped>
.painting-status {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 18px 20px;
  background: linear-gradient(135deg, rgba(201, 169, 78, 0.1), rgba(102, 51, 153, 0.08));
  border: 1px solid rgba(201, 169, 78, 0.32);
  border-radius: 14px;
  box-shadow: 0 0 22px rgba(201, 169, 78, 0.08);
  margin-top: 4px;
}
.painting-frame {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.45);
  border: 2px solid rgba(201, 169, 78, 0.45);
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.frame-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 50%, rgba(201, 169, 78, 0.32), transparent 70%);
  animation: pulse 1.6s ease-in-out infinite;
}
.spinner {
  position: relative;
  z-index: 1;
  width: 24px;
  height: 24px;
  border: 2px solid rgba(201, 169, 78, 0.25);
  border-top-color: #C9A94E;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
@keyframes pulse {
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
}
.painting-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.painting-title {
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  font-size: 14px;
  letter-spacing: 0.06em;
}
.painting-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(212, 196, 232, 0.65);
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
}
.painting-meta .dot { opacity: 0.5; }
.painting-hint {
  font-size: 11px;
  color: rgba(212, 196, 232, 0.5);
  font-style: italic;
  margin-top: 4px;
}
</style>
